import React, { useState, useEffect } from "react";
import { Card, CardBody, Typography, Button } from "@material-tailwind/react";
import SpeakerSidebar from "./SpeakerSidebar";

export interface BioType {
  children: Array<{ text: string }>;
}

export interface SpeakerContent {
  name: string;
  Role: string;
  university: string;
  profileUrl: string;
  image: { asset: { _ref: string } };
  Biography: BioType[];
  Abstract: BioType[];
  AbstractTitle: string;
}

interface CardListProps {
  data: SpeakerContent[];
}

const SpeakerCardList: React.FC<CardListProps> = ({ data }) => {
  const [selectedPerson, setSelectedPerson] = useState<SpeakerContent | null>(null);
  const [activeTab, setActiveTab] = useState<"biography" | "abstract">("biography");

  const openSidebar = (person: SpeakerContent, tab: "biography" | "abstract") => {
    setSelectedPerson(person);
    setActiveTab(tab);
  };

  // ✅ Prevent background scroll when sidebar is open
  useEffect(() => {
    if (selectedPerson) {
      // Save scroll position (to restore later)
      const scrollY = window.scrollY;
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = "100%";
      document.body.style.overflowY = "hidden";
    } else {
      // Restore scroll position
      const scrollY = document.body.style.top;
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      document.body.style.overflowY = "";
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || "0") * -1);
      }
    }
  }, [selectedPerson]);

  return (
    <>
      <div className="mx-auto max-w-7xl p-4">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {data.map((item, index) => {
            const imgRef = item.image?.asset?._ref;
            const imgUrl = imgRef
              ? `https://cdn.sanity.io/images/79vgbaej/production/${imgRef.slice(6, -4)}.${imgRef.slice(-3)}`
              : "";

            return (
              <Card
                key={index}
                className="w-full h-[28rem] rounded-2xl shadow-lg flex flex-col"
              >
                <div className="flex justify-center items-center py-4 bg-transparent">
                  {imgUrl && (
                    <img
                      src={imgUrl}
                      alt={item.name}
                      className="h-40 w-40 object-cover rounded-lg border-4 border-gray-200 shadow-md"
                    />
                  )}
                </div>

                <CardBody className="flex-1 flex flex-col items-center pt-0">
                  <Typography className="mb-2 text-lg font-bold text-blue-gray-800 text-center">
                    {item.name}
                  </Typography>
                  <Typography className="text-base font-semibold text-blue-gray-700 text-center">
                    {item.Role}
                  </Typography>
                  <Typography className="mt-1 text-sm text-blue-gray-600 text-center mb-4">
                    {item.university}
                  </Typography>

                  <div className="flex-grow"></div>

                  <div className="w-full space-y-3 mt-4">
                    <div className="flex justify-center">
                      <Button
                        color="red"
                        variant="outlined"
                        className="w-1/2 rounded-full py-1 text-sm font-semibold"
                        onClick={() => openSidebar(item, "biography")}
                      >
                        Biography
                      </Button>
                    </div>

                    <Button
                      color="red"
                      className="w-full rounded-full py-3 text-base font-semibold"
                      onClick={() => openSidebar(item, "abstract")}
                    >
                      Abstract
                    </Button>
                  </div>
                </CardBody>
              </Card>
            );
          })}
        </div>
      </div>

      {selectedPerson && (
        <SpeakerSidebar
          isOpen={!!selectedPerson}
          onClose={() => setSelectedPerson(null)}
          person={selectedPerson}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />
      )}
    </>
  );
};

export default SpeakerCardList;
